import OrderClientProposalApiClient from '@/api/OrderClientProposalApiClient';
import { i18n } from '@/i18n';
import { ORDER_PROPOSAL_STATUS } from '@/typings/enums/ORDER_PROPOSAL_STATUS';
import { useNotif, usePrompt } from '@orion.ui/orion';

class OrderClientProposalService {

	async getDetailsAsync (
		...args: Parameters<typeof OrderClientProposalApiClient.getDetails>
	) {
		const { data } = await OrderClientProposalApiClient.getDetails(...args);
		return data;
	}

	async acceptProposalAsync (
		orderClientProposals: IOrderClientProposalWithUserModel[],
	) {
		const ids = orderClientProposals.mapKey('id');
		const { confirm, value: comment } = await usePrompt<string>({
			title: i18n.global.t('common.label.confirmation'),
			message: `${i18n.global.t('common.confirmAcceptCandidate', ids.length)}
								<div class="arm-alert arm-alert--info arm-alert--center" style="margin:10px auto 20px">
									${i18n.global.t('business.addCommentForAgency')}
								</div>`,
			prompt: {
				type: 'textarea',
				fieldProps: { label: i18n.global.t('common.label.optionalComment') },
			},
		});

		if (confirm) {
			await OrderClientProposalApiClient.update({
				ids,
				hasAccepted: true,
				comment: comment === null ? undefined : comment,
			});

			orderClientProposals.forEach((x) => {
				x.responseDate = new Date().toString();
				x.status = ORDER_PROPOSAL_STATUS.Accepted;
			});

			useNotif.success(
				ids.length > 1
					? i18n.global.t('business.candidatesAcceptedSuccessfully')
					: i18n.global.t('business.talentPool.candidateAccepted'),
			);
		}

		return {
			confirm,
			comment,
		};
	}

	async declineProposalAsync (
		orderClientProposals: IOrderClientProposalWithUserModel[],
	) {
		const ids = orderClientProposals.mapKey('id');
		const { confirm, value: comment } = await usePrompt<string>({
			title: i18n.global.t('common.label.confirmation'),
			message: i18n.global.t('common.confirmRefuseCandidate', ids.length),
			prompt: {
				type: 'textarea',
				fieldProps: { label: i18n.global.t('business.optionalCommentForAgency') },
			},
		});

		if (confirm) {
			await OrderClientProposalApiClient.update({
				ids,
				hasAccepted: false,
				comment: comment === null ? undefined : comment,
			});

			orderClientProposals.forEach((x) => {
				x.responseDate = new Date().toString();
				x.status = ORDER_PROPOSAL_STATUS.Refused;
			});

			useNotif.success(
				ids.length > 1
					? i18n.global.t('business.candidatesRefusedSuccessfully')
					: i18n.global.t('business.talentPool.candidateRemoved'),
			);
			return { comment };
		}

		return {
			confirm,
			comment,
		};
	}

	async gridAsync (
		...args: Parameters<typeof OrderClientProposalApiClient.grid>
	) {
		const { data } = await OrderClientProposalApiClient.grid(...args);
		return data;
	}

	async countAsync (...args: Parameters<typeof OrderClientProposalApiClient.count>) {
		const { data } = await OrderClientProposalApiClient.count(...args);
		return data;
	}

}

const orderClientProposalServiceSingleton = new OrderClientProposalService();

export default function useOrderClientProposalService () {
	return orderClientProposalServiceSingleton;
}
